import React, { useState, useContext, useEffect, useCallback, useMemo } from "react";
import StarekContext from "../../context/StarekContext";
import { gql, useQuery, useMutation } from "@apollo/client";
import ProductionReport from "./ProductionReport";
import EfficiencyReport from "./EfficiencyReport";
import RawOnOffReport from "./RawOnOffReport";
import OperationReport from "./OperationReport";
import ReportFilter from "./ReportFilter";
import ReportRangeSelector from "./ReportRangeSelector";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BoltIcon from "@mui/icons-material/Bolt";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SaveFavReportsSidebar from "./SaveFavReportsSidebar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { useSearchParams } from "react-router-dom";

const TABS = {
    PRODUCTION: "production",
    EFFICIENCY: "efficiency",
    RAW: "raw-on-off",
    OPERATION: "operation",
};

const SUBSCRIPTION_REPORT_LIMIT = 100;
const SUBSCRIPTION_START_DAY = 1;

function ReportHeader({ activeTab, onChangeTab }) {
    const baseButton =
        "tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-text-xs tw-rounded-md";

    const activeClasses =
        "tw-bg-blue-50 tw-text-slate-900 tw-font-medium tw-border tw-border-blue-500";
    const inactiveClasses =
        "tw-text-slate-700 hover:tw-bg-slate-100 tw-border tw-border-transparent";

    const tabButton = (tabKey, label, Icon) => (
        <button
            type="button"
            className={`${baseButton} ${activeTab === tabKey ? activeClasses : inactiveClasses
                }`}
            onClick={() => onChangeTab(tabKey)}
        >
            <Icon fontSize="small" className="tw-opacity-90" />
            {label}
        </button>
    );

    const toggleProdEff = () => {
        if (activeTab === TABS.PRODUCTION) return onChangeTab(TABS.EFFICIENCY);
        if (activeTab === TABS.EFFICIENCY) return onChangeTab(TABS.PRODUCTION);
    };

    const toggleEffRaw = () => {
        if (activeTab === TABS.EFFICIENCY) return onChangeTab(TABS.RAW);
        if (activeTab === TABS.RAW) return onChangeTab(TABS.EFFICIENCY);
    };

    const ToggleButton = ({ onClick }) => (
        <button
            type="button"
            onClick={onClick}
            className="tw-inline-flex tw-items-center tw-justify-center tw-h-7 tw-w-7"
        >
            <SwapHorizIcon fontSize="small" className="tw-text-slate-500" />
        </button>
    );

    return (
        <div className="tw-flex tw-items-center tw-border-b tw-border-slate-200 tw-pb-2 tw-gap-3">
            {tabButton(TABS.PRODUCTION, "Production Report", BoltIcon)}
            <ToggleButton onClick={toggleProdEff} />
            {tabButton(TABS.EFFICIENCY, "Efficiency Report", EqualizerIcon)}
            <ToggleButton onClick={toggleEffRaw} />
            {tabButton(TABS.RAW, "Raw On-Off Report", FiberManualRecordIcon)}
            {tabButton(TABS.OPERATION, "Operation Report", AccountTreeIcon)}
        </div>
    );
}

function formatDateForSummary(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString(undefined, {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function buildViewingSummary(activeTab, filters) {
    if (!filters || !filters.devices) return "Select filters from to view data";
    const reportName =
        activeTab === TABS.PRODUCTION
            ? "Production Report"
            : activeTab === TABS.EFFICIENCY
                ? "Efficiency Report"
                : activeTab === TABS.OPERATION
                    ? "Operation Report"
                    : "Raw On-Off Report";

    let devicesPart = "for all devices";

    if (filters.devices) {
        const arr = filters.devices.split(",");

        if (arr.length === 1) {
            devicesPart = `for ${arr[0]}`;
        } else if (arr.length === 2) {
            devicesPart = `for ${arr[0]}, ${arr[1]}`;
        } else if (arr.length > 2) {
            devicesPart = (
                <>
                    for {arr[0]}, {arr[1]} and{" "}
                    <span
                        className="tw-underline tw-cursor-pointer"
                        title={arr.join(", ")}
                    >
                        +{arr.length - 2}
                    </span>
                </>
            );
        }
    }

    let durationPart = "";

    if (filters.duration?.startsWith("shift_")) {
        durationPart = `between shift on ${filters.shiftDate}`;
    } else if (filters.duration === "custom") {
        durationPart = `between ${formatDateForSummary(
            filters.customStart,
        )} and ${formatDateForSummary(filters.customEnd)}`;
    } else {
        durationPart = `for last ${filters.duration
            .replace("h", " hours")
            .replace("d", " days")}`;
    }

    return (
        <>
            You are viewing {reportName} {devicesPart} {durationPart}
        </>
    );
}

const GET_DEVICES_IN_PLANT = gql`
    query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
        shadowDevices(where: $where) {
            id
            name
            device
            parameters
            jobSchedules {
                id
                name
                start_time
                end_time
                part {
                    id
                    name
                }
            }
        }
    }
`;

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

const CREATE_FAVORITE_REPORT = gql`
    mutation CreateFavoriteReport($data: FavoriteReportsCreateInput!) {
        createOneFavoriteReports(data: $data) {
            id
            name
            parameters
            user {
                id
            }
            description
            plant {
                id
            }
        }
    }
`;

export default function Reports() {
    const [searchParams] = useSearchParams();
    // ?tab= lets other screens send the user to a specific report — the Operations
    // layer points here once a run is scheduled.
    const requestedTab = searchParams.get("tab");
    const [activeTab, setActiveTab] = useState(
        Object.values(TABS).includes(requestedTab) ? requestedTab : TABS.RAW,
    );
    const { can } = usePermissions();
    const canWrite = can("report", "WRITE");
    const [isFilterOpen, setIsFilterOpen] = useState(true);
    const [showWizard, setShowWizard] = useState(false);
    const [cachedDatapoints, setCachedDatapoints] = useState(null);
    const [isLoadingDatapoints, setIsLoadingDatapoints] = useState(false);
    const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(0);
    const [shouldTrackUsage, setShouldTrackUsage] = useState(false);

    const handleOpenWizard = () => {
        setShowWizard(true);
    };
    const handleCloseWizard = () => {
        setShowWizard(false);
    };
    const handleSaveReport = async (data) => {
        try {
            const reportParams = {
                filters,
                search_params: {
                    range,
                },
            };

            await createFavoriteReport({
                variables: {
                    data: {
                        name: data.name,
                        user: { connect: { id: parseInt(user?.id) } },
                        plant: { connect: { id: parseInt(lastUsedPlant?.id) } },
                        parameters: reportParams,
                        description: data.description,
                    },
                },
            });

            showInfo("Report saved to Snapshots successfully");
            setShowWizard(false);
        } catch (error) {
            console.error("Error saving report", error);
            showError("Failed to save report: " + error.message);
        }
    };

    const GET_CAPABILITIES_FOR_OPERATIONS = gql`
        query GetCapabilitiesForOperations($operationIds: [Int!]!) {
            capabilities(where: { operation_id: { in: $operationIds } }) {
                device_id
                operation_id
            }
        }
    `;

    const [filters, setFilters] = useState(() => {
        try {
            const saved = localStorage.getItem("starek_report_filters");
            return saved
                ? JSON.parse(saved)
                : {
                    devices: "",
                    interval: "",
                    duration: "",
                    shiftDate: "",
                };
        } catch (e) {
            console.error("Failed to parse filters from local storage", e);
            return {
                devices: "",
                interval: "",
                duration: "",
                shiftDate: "",
            };
        }
    });

    const {
        lastUsedWorkspace,
        auth,
        lastUsedPlant,
        user,
        showInfo,
        showError,
        setAuth,
        unauthorizedFlag,
        setUnauthorizedFlag,
    } = useContext(StarekContext);

    const [createFavoriteReport] = useMutation(CREATE_FAVORITE_REPORT);

    const { data: deviceData, refetch: refetchDevices } = useQuery(
        GET_DEVICES_IN_PLANT,
        {
            variables: {
                where: {
                    plant: {
                        is: {
                            id: {
                                equals: lastUsedPlant?.id,
                            },
                        },
                    },
                },
            },
            skip: !lastUsedPlant?.id,
        },
    );

    const { data: shiftData } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
        variables: { plantId: lastUsedPlant?.id },
        skip: !lastUsedPlant?.id,
    });

    useEffect(() => {
        if (!shiftData?.shifts || !deviceData?.shadowDevices) return;
        if (filters.devices) return;

        const now = new Date();
        const todayStr = now.toISOString().slice(0, 10);

        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);

        const currentSeconds =
            now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

        const completedShifts = shiftData.shifts.filter((shift) => {
            return shift.end_time < currentSeconds;
        });

        let targetShift = null;
        let selectedDateStr = todayStr;

        if (completedShifts.length > 0) {
            completedShifts.sort((a, b) => b.end_time - a.end_time);
            targetShift = completedShifts[0];
            selectedDateStr = todayStr;
        } else {
            if (shiftData.shifts.length > 0) {
                targetShift = shiftData.shifts[shiftData.shifts.length - 1];
                selectedDateStr = yesterdayStr;
            }
        }

        if (targetShift) {
            const allDeviceIds = deviceData.shadowDevices
                .map((d) => d.id)
                .join(",");
            let diff = targetShift.end_time - targetShift.start_time;
            if (diff < 0) diff += 86400;

            setFilters({
                devices: allDeviceIds,
                duration: `shift_${targetShift.id}`,
                shiftDate: selectedDateStr,
                interval: "",
                durationStart: null,
            });

            const base = new Date(selectedDateStr);
            const startDate = new Date(base);
            const endDate = new Date(base);

            startDate.setSeconds(targetShift.start_time);
            endDate.setSeconds(targetShift.end_time);

            if (targetShift.end_time < targetShift.start_time) {
                endDate.setDate(endDate.getDate() + 1);
            }

            setFilters((prev) => ({
                ...prev,
                durationStart: startDate.toISOString(),
                durationEnd: endDate.toISOString(),
                durationSeconds: diff,
            }));
            setRange([0, diff]);
            setCommittedRange([0, diff]);
        }
    }, [shiftData, deviceData, filters.devices]);

    const [range, setRange] = useState(() => {
        try {
            const saved = localStorage.getItem("starek_report_range");
            return saved ? JSON.parse(saved) : [0, 0];
        } catch (e) {
            console.error("Failed to parse range from local storage", e);
            return [0, 0];
        }
    });

    const [committedRange, setCommittedRange] = useState(range);

    const selectedOperationIds = (filters.operations || "")
        .split(",")
        .filter(Boolean)
        .map(Number);

    const { data: capabilityData } = useQuery(GET_CAPABILITIES_FOR_OPERATIONS, {
        skip: selectedOperationIds.length === 0,
        fetchPolicy: "network-only",
        variables: { operationIds: selectedOperationIds },
    });

    /**
     * Filters restricted to the machines that perform the selected operations.
     * With no operation selected this is exactly the filters as chosen, so the
     * existing reports behave as before.
     */
    const effectiveFilters = useMemo(() => {
        if (selectedOperationIds.length === 0) return filters;
        const capable = new Set(
            (capabilityData?.capabilities || []).map((c) => c.device_id),
        );
        const narrowed = (filters.devices || "")
            .split(",")
            .filter((id) => id && (id.startsWith("shift_") || capable.has(id)));
        return { ...filters, devices: narrowed.join(",") };
    }, [filters, capabilityData?.capabilities, selectedOperationIds.length]);

    useEffect(() => {
        localStorage.setItem("starek_report_filters", JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        localStorage.setItem("starek_report_range", JSON.stringify(range));
    }, [range]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleReset = () => {
        const defaultFilters = {
            devices: "",
            interval: "",
            duration: "",
        };
        setFilters(defaultFilters);
        setRange([0, 0]);
        setCommittedRange([0, 0]);
        localStorage.removeItem("starek_report_filters");
        localStorage.removeItem("starek_report_range");
    };

    const formatDateTime = (d) =>
        d.toLocaleString(undefined, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });

    const formatTimeOnly = (d) =>
        d.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        });

    function getWindowLabel(filters, range) {
        const { duration, durationStart } = filters || {};
        if (!durationStart || !Array.isArray(range) || range.length !== 2) {
            return "Window not set";
        }

        const base = new Date(durationStart);
        if (Number.isNaN(base.getTime())) return "Window not set";

        const [startOffsetSec, endOffsetSec] = range;
        const start = new Date(base.getTime() + startOffsetSec * 1000);
        const end = new Date(base.getTime() + endOffsetSec * 1000);

        const isShift = duration.startsWith("shift_");
        const isCustom = duration === "custom";
        const isByDuration = !isShift && !isCustom;

        if (isCustom || isShift || isByDuration) {
            return `${formatDateTime(start)} – ${formatDateTime(end)}`;
        }

        return "Window not set";
    }

    const handleApply = (payload) => {
        const duration = payload?.duration || "";
        const isShift = duration.startsWith("shift_");
        const isCustom = duration === "custom";
        const isByDuration = !isShift && !isCustom && duration;

        let nextPayload = { ...payload };

        if (isByDuration) {
            const now = new Date();
            const durationVal = parseInt(duration, 10);
            const unit = duration.slice(-1);
            let seconds = 0;

            if (unit === "h") seconds = durationVal * 3600;
            if (unit === "d") seconds = durationVal * 86400;

            const start = new Date(now.getTime() - seconds * 1000);

            nextPayload = {
                ...nextPayload,
                durationStart: start.toISOString(),
                durationEnd: now.toISOString(),
                durationSeconds: seconds,
            };
        }

        setFilters(nextPayload);

        const windowSize =
            nextPayload.intervalSeconds || nextPayload.durationSeconds || 0;
        const windowDuration = nextPayload.durationSeconds || windowSize || 0;
        const clampedWindow = Math.min(windowSize, windowDuration);

        const start = windowDuration - clampedWindow;
        const end = windowDuration;

        setRange([start, end]);
        setCommittedRange([start, end]);
        setShouldTrackUsage(true);
        setIsFilterOpen(false);
    };

    const refreshData = async () => {
        const { duration } = filters;
        const isShift = duration?.startsWith("shift_");
        const isCustom = duration === "custom";
        const isRelative = !isShift && !isCustom && duration;

        if (isRelative) {
            const now = new Date();
            const durationVal = parseInt(duration);
            const unit = duration.slice(-1); // 'h' or 'd'

            let seconds = 0;
            if (unit === "h") seconds = durationVal * 3600;
            if (unit === "d") seconds = durationVal * 86400;

            const end = now;
            const start = new Date(now.getTime() - seconds * 1000);

            setFilters((prev) => ({
                ...prev,
                durationStart: start.toISOString(),
                durationEnd: end.toISOString(),
                durationSeconds: seconds,
            }));

            const windowSize = filters.intervalSeconds || seconds || 0;
            const clampedWindow = Math.min(windowSize, seconds || 0);
            const windowStart = (seconds || 0) - clampedWindow;
            const windowEnd = seconds || 0;

            setRange([windowStart, windowEnd]);
            setCommittedRange([windowStart, windowEnd]);
            return true;
        }
        return false;
    };

    const fetchRawData = useCallback(async (trackUsage = false) => {
        if (!filters.devices || !filters.durationStart || !committedRange) {
            console.warn("Missing filters for raw data fetch");
            return null;
        }

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        let cycleMonth = currentMonth;
        let cycleYear = currentYear;
        if (now.getDate() < SUBSCRIPTION_START_DAY) {
            cycleMonth = currentMonth - 1;
            if (cycleMonth < 0) {
                cycleMonth = 11;
                cycleYear = currentYear - 1;
            }
        }
        const cycleKey = `${cycleYear}-${cycleMonth}`;

        let usageStr = localStorage.getItem("starek_report_usage");
        let usage = usageStr ? JSON.parse(usageStr) : { cycle: cycleKey, count: 0 };

        if (usage.cycle !== cycleKey) {
            usage = { cycle: cycleKey, count: 0 };
        }

        if (trackUsage && usage.count >= SUBSCRIPTION_REPORT_LIMIT) {
            showError(`Monthly report generation limit (${SUBSCRIPTION_REPORT_LIMIT}) reached. Please upgrade your subscription.`);
            return null;
        }

        const { scheme, host, port } = lastUsedWorkspace.engine;
        const workspaceId = lastUsedWorkspace.name;

        const baseTime = new Date(filters.durationStart).getTime();
        const startTime = Math.floor(
            (baseTime + committedRange[0] * 1000) / 1000,
        );
        const endTime = Math.floor(
            (baseTime + committedRange[1] * 1000) / 1000,
        );

        const url = `${scheme}://${host}:${port}/api/v1/${workspaceId}/datapoints`;

        const payload = {
            devices: effectiveFilters.devices
                .split(",")
                .map((d) => d.trim())
                .filter(Boolean),
            start_time: startTime,
            end_time: endTime,
            group_by_device: true,
            group_by_signal: true,
        };

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${auth}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setUnauthorizedFlag(true);
                    return null;
                }
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            // Update cache
            setCachedDatapoints(data);

            if (trackUsage) {
                usage.count += 1;
                localStorage.setItem("starek_report_usage", JSON.stringify(usage));
            }

            return data;
        } catch (error) {
            console.error("Error fetching raw data:", error);
            throw error;
        }
    }, [filters, effectiveFilters, committedRange, lastUsedWorkspace, auth]);

    // Auto-fetch datapoints when filters or range changes
    useEffect(() => {
        if (!filters.devices || !filters.durationStart || !committedRange) {
            return;
        }

        setIsLoadingDatapoints(true);
        fetchRawData(shouldTrackUsage)
            .catch(() => {
                // Error handling is done in fetchRawData
            })
            .finally(() => {
                setIsLoadingDatapoints(false);
                if (shouldTrackUsage) setShouldTrackUsage(false);
            });
    }, [filters, committedRange, fetchRawData, shouldTrackUsage]);

    const handleDownloadCSV = async () => {
        try {
            const data = await fetchRawData();
            if (!data || !data.success || !data.data) {
                showError("No data available to download");
                return;
            }

            const rows = [["Timestamp", "Device", "Signal", "Value"]];
            const devices = data.data;

            Object.keys(devices).forEach((deviceName) => {
                const deviceSignals = devices[deviceName];
                Object.keys(deviceSignals).forEach((signalKey) => {
                    const points = deviceSignals[signalKey];
                    if (Array.isArray(points)) {
                        points.forEach((p) => {
                            const timeStr = new Date(
                                p.time * 1000,
                            ).toISOString();
                            rows.push([
                                timeStr,
                                deviceName,
                                signalKey,
                                p.measure_value,
                            ]);
                        });
                    }
                });
            });

            if (rows.length === 1) {
                showInfo("No datapoints found in the selected range");
                return;
            }

            const csvContent = rows.map((e) => e.join(",")).join("\n");
            const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `report_${new Date().getTime()}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            console.error("Download CSV failed", e);
            showError("Failed to download CSV");
        }
    };

    const handleEfficiencyBarClick = (data) => {
        setActiveTab(TABS.RAW);
    };

    return (
        <div className="tw-relative tw-flex tw-h-full tw-min-h-[calc(100vh-80px)] tw-bg-slate-50">
            {!isFilterOpen && (
                <button
                    type="button"
                    onClick={() => setIsFilterOpen(true)}
                    className="tw-left-0 tw-top-16 tw-h-10 tw-z-20 tw-inline-flex tw-items-start tw-justify-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-p-2 hover:tw-bg-slate-100"
                    title="Filters"
                >
                    <ChevronRightIcon />
                </button>
            )}

            {isFilterOpen && (
                <div className="tw-w-1/4 tw-max-w-[25%] tw-border-r tw-border-slate-200 tw-bg-white tw-p-4 tw-flex tw-flex-col">
                    <ReportFilter
                        filters={filters}
                        onChange={handleFilterChange}
                        onReset={handleReset}
                        onApply={handleApply}
                        onClose={() => setIsFilterOpen(false)}
                    />
                </div>
            )}

            <div className="tw-flex-1 tw-flex tw-flex-col tw-p-4 tw-gap-4">
                <ReportHeader
                    activeTab={activeTab}
                    onChangeTab={setActiveTab}
                />
                <div
                    className="tw-text-xs tw-text-slate-700 tw-cursor-pointer hover:tw-underline"
                    onClick={() => setIsFilterOpen((prev) => !prev)}
                    title="Toggle Filters"
                >
                    {buildViewingSummary(activeTab, filters)}
                </div>

                <div className="tw-flex-1 tw-bg-white tw-shadow-sm tw-rounded-lg tw-p-4 tw-flex tw-flex-col tw-gap-4">
                    {activeTab === TABS.PRODUCTION && (
                        <ProductionReport
                            filters={effectiveFilters}
                            range={committedRange}
                            deviceData={deviceData}
                            cachedDatapoints={cachedDatapoints}
                            isLoadingDatapoints={isLoadingDatapoints}
                            selectedDeviceIndex={selectedDeviceIndex}
                            onSelectedDeviceIndexChange={setSelectedDeviceIndex}
                            onRefresh={refetchDevices}
                            onFavoriteClick={canWrite ? handleOpenWizard : null}
                            onDownloadCSV={handleDownloadCSV}
                        />
                    )}
                    {activeTab === TABS.EFFICIENCY && (
                        <EfficiencyReport
                            filters={effectiveFilters}
                            range={committedRange}
                            cachedDatapoints={cachedDatapoints}
                            isLoadingDatapoints={isLoadingDatapoints}
                            selectedDeviceIndex={selectedDeviceIndex}
                            onSelectedDeviceIndexChange={setSelectedDeviceIndex}
                            fetchRawData={fetchRawData}
                            onRefresh={refreshData}
                            shadowDevices={deviceData?.shadowDevices || []}
                            onFavoriteClick={canWrite ? handleOpenWizard : null}
                            onDownloadCSV={handleDownloadCSV}
                            onBarClick={handleEfficiencyBarClick}
                        />
                    )}
                    {activeTab === TABS.OPERATION && (
                        <OperationReport
                            filters={filters}
                            range={committedRange}
                            onRefresh={refetchDevices}
                            onFavoriteClick={canWrite ? handleOpenWizard : null}
                            onDownloadCSV={handleDownloadCSV}
                        />
                    )}
                    {activeTab === TABS.RAW && (
                        <RawOnOffReport
                            filters={effectiveFilters}
                            range={committedRange}
                            cachedDatapoints={cachedDatapoints}
                            isLoadingDatapoints={isLoadingDatapoints}
                            selectedDeviceIndex={selectedDeviceIndex}
                            onSelectedDeviceIndexChange={setSelectedDeviceIndex}
                            fetchRawData={fetchRawData}
                            onRefresh={refreshData}
                            shadowDevices={deviceData?.shadowDevices || []}
                            onFavoriteClick={canWrite ? handleOpenWizard : null}
                            onDownloadCSV={handleDownloadCSV}
                        />
                    )}
                    <ReportRangeSelector
                        value={range}
                        onChange={setRange}
                        onChangeCommitted={setCommittedRange}
                        intervalSeconds={filters.intervalSeconds}
                        durationSeconds={filters.durationSeconds}
                        label={getWindowLabel(filters, range)}
                        startDate={filters.durationStart}
                    />
                </div>
            </div>

            <SaveFavReportsSidebar
                show={showWizard}
                handleClose={handleCloseWizard}
                onSave={handleSaveReport}
            />

            <ConfirmModal
                open={!!unauthorizedFlag}
                type="warning"
                title="Session Expired"
                content="Your session has expired. Please log in again to continue."
                showCloseButton={false}
                customButtons={[{ label: "Login", action: "confirm" }]}
                onConfirm={() => {
                    setUnauthorizedFlag(false);
                    setAuth(null);
                    window.location.href = "/login";
                }}
            />
        </div>
    );
}
